import React, { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  AppBar,
  Avatar,
  Box,
  Chip,
  Collapse,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  ExpandLess,
  ExpandMore,
  Home as HomeIcon,
  Logout,
  Menu as MenuIcon,
} from '@mui/icons-material';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { companyApi } from '../services/api';
import { useDomainSettingsQuery } from '../settings/useDomainSettingsQuery';
import {
  COMPANY_DASHBOARD_PATH,
  COMPANY_NAVIGATION_STORAGE_KEY,
  CompanyNavigationGroupId,
  getCompanyActiveNavigationGroupId,
  getCompanyNavigationGroups,
  getCompanyPageTitle,
} from '../navigation/companyNavigation';
import { APP_ROUTES } from '../routing/appRoutes';
import { STATIC_BASE_URL } from '../utils/runtimeUrls';
import { LanguageSwitcher } from './LanguageSwitcher';
import { NotificationBell } from './notifications/NotificationBell';
import { BrandMark } from './BrandMark';

const drawerWidth = 272;
const drawerDividerWidth = 1;
const appBarHeight = 64;
const readInitialExpandedGroupId = (
  groupIds: CompanyNavigationGroupId[],
  activeGroupId: CompanyNavigationGroupId | null
): CompanyNavigationGroupId | null => {
  if (typeof window === 'undefined') {
    return activeGroupId;
  }

  try {
    const storedGroupId = window.sessionStorage.getItem(COMPANY_NAVIGATION_STORAGE_KEY);

    if (!storedGroupId) {
      return activeGroupId;
    }

    const parsedGroupId = JSON.parse(storedGroupId);
    const normalizedGroupId =
      typeof parsedGroupId === 'string' && groupIds.includes(parsedGroupId as CompanyNavigationGroupId)
        ? (parsedGroupId as CompanyNavigationGroupId)
        : Array.isArray(parsedGroupId)
          ? groupIds.find((groupId) => parsedGroupId.includes(groupId)) ?? null
          : null;

    return activeGroupId ?? normalizedGroupId;
  } catch {
    return activeGroupId;
  }
};

export const CompanyLayout: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [desktopSidebarOpen, setDesktopSidebarOpen] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const companyId = user?.company?.id;
  const userRole = user?.role;
  const userPermissions = user?.permissions;

  const { data: companySettings } = useQuery(
    ['companySettings', companyId],
    () => companyApi.getSettings(),
    {
      enabled: !!companyId,
    }
  );
  const { data: domainSettings } = useDomainSettingsQuery(companyId);
  const navigationFeatures = useMemo(
    () => ({
      multipleLocationsEnabled: Boolean(domainSettings?.multipleLocations),
      emailConnectivityEnabled: domainSettings?.emailConnectivity !== false,
    }),
    [domainSettings?.multipleLocations, domainSettings?.emailConnectivity]
  );
  const navigationGroups = useMemo(
    () => getCompanyNavigationGroups(t, userRole, userPermissions, 'sidebar', navigationFeatures),
    [navigationFeatures, t, userPermissions, userRole]
  );
  const navigationGroupIds = useMemo(
    () => navigationGroups.map((group) => group.id),
    [navigationGroups]
  );
  const activeGroupId = getCompanyActiveNavigationGroupId(
    location.pathname,
    userRole,
    navigationFeatures
  );
  const [expandedGroupId, setExpandedGroupId] = useState<CompanyNavigationGroupId | null>(() =>
    readInitialExpandedGroupId(navigationGroupIds, activeGroupId)
  );

  useEffect(() => {
    setExpandedGroupId((currentGroupId) => {
      if (!currentGroupId || navigationGroupIds.includes(currentGroupId)) {
        return currentGroupId;
      }

      return null;
    });
  }, [navigationGroupIds]);

  useEffect(() => {
    if (!activeGroupId) {
      return;
    }

    setExpandedGroupId((currentGroupId) =>
      currentGroupId === activeGroupId ? currentGroupId : activeGroupId
    );
  }, [activeGroupId, location.pathname]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    if (expandedGroupId) {
      window.sessionStorage.setItem(
        COMPANY_NAVIGATION_STORAGE_KEY,
        JSON.stringify(expandedGroupId)
      );
      return;
    }

    window.sessionStorage.removeItem(COMPANY_NAVIGATION_STORAGE_KEY);
  }, [expandedGroupId]);

  const handleDrawerToggle = () => {
    setMobileOpen((open) => !open);
  };

  const handleDesktopSidebarToggle = () => {
    setDesktopSidebarOpen((open) => !open);
  };

  const handleLogout = () => {
    logout();
    navigate(APP_ROUTES.public.search);
  };

  const handleCompanyHome = () => {
    navigate(COMPANY_DASHBOARD_PATH);
  };

  const handleGroupToggle = (groupId: CompanyNavigationGroupId) => {
    setExpandedGroupId((currentGroupId) =>
      currentGroupId === groupId ? null : groupId
    );
  };

  const handleNavigationItemClick = (event: React.MouseEvent<HTMLElement>) => {
    if (
      !isMobile ||
      event.defaultPrevented ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }

    setMobileOpen(false);
  };

  const pageTitle = getCompanyPageTitle(t, location.pathname, userRole, navigationFeatures);
  const companyName = companySettings?.company?.companyName || user?.company?.name;
  const companyLogoUrl = companySettings?.company?.logoUrl
    ? (
        companySettings.company.logoUrl.startsWith('http')
          ? companySettings.company.logoUrl
          : `${STATIC_BASE_URL}${companySettings.company.logoUrl}`
      )
    : null;
  const desktopAppBarOffset = desktopSidebarOpen ? drawerWidth - drawerDividerWidth : 0;
  const desktopSidebarWidth = desktopSidebarOpen ? drawerWidth : 0;
  const desktopSidebarToggleLabel = desktopSidebarOpen
    ? t('nav.hideSidebar', 'Hide sidebar')
    : t('nav.showSidebar', 'Show sidebar');

  const drawerHeader = (
    <Toolbar
      sx={{
        background: 'linear-gradient(135deg, #0A0F1C 0%, #102756 100%)',
        color: 'white',
        minHeight: `${appBarHeight}px !important`,
        px: 2,
      }}
    >
      <BrandMark
        compact
        showTagline={false}
        tone="light"
        sx={{ flexGrow: 1, minWidth: 0 }}
      />
      <Tooltip
        title={
          isMobile
            ? t('nav.closeSidebar', 'Close sidebar')
            : desktopSidebarToggleLabel
        }
      >
        <IconButton
          color="inherit"
          edge="end"
          size="small"
          aria-label={
            isMobile
              ? t('nav.closeSidebar', 'Close sidebar')
              : desktopSidebarToggleLabel
          }
          onClick={isMobile ? handleDrawerToggle : handleDesktopSidebarToggle}
        >
          <MenuIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Toolbar>
  );

  const drawerBody = (
    <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, overflowY: 'auto' }}>
      <Box
        sx={{
          p: 2,
          backgroundColor: alpha(theme.palette.common.white, 0.05),
          color: 'common.white',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Avatar sx={{ bgcolor: 'primary.main', mr: 1, color: 'common.white' }}>
            {user?.firstName?.[0]}{user?.lastName?.[0]}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" fontWeight="medium" noWrap>
              {user?.firstName} {user?.lastName}
            </Typography>
            <Typography
              variant="caption"
              noWrap
              sx={{ color: alpha(theme.palette.common.white, 0.72) }}
            >
              {user?.role === 'SUPERUSER'
                ? t('roles.superuser', 'Superuser')
                : user?.role === 'ADMIN'
                  ? t('roles.admin', 'Admin')
                  : t('roles.user', 'User')}
            </Typography>
          </Box>
        </Box>
        {user?.company && (
          <Chip
            label={user.company.name}
            size="small"
            sx={{
              width: '100%',
              mt: 1,
              color: 'common.white',
              backgroundColor: alpha(theme.palette.common.white, 0.08),
              borderColor: alpha(theme.palette.common.white, 0.12),
            }}
          />
        )}
      </Box>

      <Divider />

      <List
        component="nav"
        aria-label={t('nav.company', 'Company')}
        sx={{ px: 1, py: 1.25 }}
      >
        {navigationGroups.map((group) => {
          const isGroupExpanded = expandedGroupId === group.id;
          const isGroupActive = activeGroupId === group.id;
          const GroupIcon = group.icon;

          return (
            <Box key={group.id} sx={{ mb: 0.75 }}>
              <ListItem disablePadding>
                <ListItemButton
                  selected={isGroupActive}
                  onClick={() => handleGroupToggle(group.id)}
                  aria-expanded={isGroupExpanded}
                  aria-controls={`${group.id}-navigation-items`}
                  sx={{
                    borderRadius: 2.25,
                    minHeight: 44,
                    border: '1px solid',
                    borderColor: isGroupActive
                      ? alpha(theme.palette.primary.main, 0.28)
                      : alpha(theme.palette.common.white, 0.08),
                    backgroundColor: isGroupActive
                      ? alpha(theme.palette.primary.main, 0.12)
                      : isGroupExpanded
                        ? alpha(theme.palette.common.white, 0.06)
                        : alpha(theme.palette.common.white, 0.02),
                    color: alpha(theme.palette.common.white, 0.92),
                    '&.Mui-selected': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.12),
                      color: 'common.white',
                    },
                    '&.Mui-selected:hover': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.18),
                    },
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.common.white, 0.08),
                    },
                    '&.Mui-focusVisible': {
                      outline: `2px solid ${alpha(theme.palette.primary.main, 0.28)}`,
                      outlineOffset: 1,
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 40,
                      color: isGroupActive
                        ? 'info.light'
                        : alpha(theme.palette.common.white, 0.72),
                    }}
                  >
                    <GroupIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={group.title}
                    primaryTypographyProps={{
                      fontSize: '0.95rem',
                      fontWeight: isGroupActive ? 700 : 600,
                      color: 'inherit',
                    }}
                  />
                  {isGroupExpanded ? (
                    <ExpandLess fontSize="small" color={isGroupActive ? 'primary' : 'inherit'} />
                  ) : (
                    <ExpandMore fontSize="small" color={isGroupActive ? 'primary' : 'inherit'} />
                  )}
                </ListItemButton>
              </ListItem>

              <Collapse in={isGroupExpanded} timeout="auto">
                <List
                  id={`${group.id}-navigation-items`}
                  component="div"
                  disablePadding
                  aria-label={group.title}
                  sx={{ mt: 0.5 }}
                >
                  {group.items.map((item) => {
                    const isActive = location.pathname === item.path;
                    const ItemIcon = item.icon;

                    return (
                      <ListItem key={item.id} disablePadding sx={{ mb: 0.35 }}>
                        <ListItemButton
                          component={RouterLink}
                          to={item.path}
                          selected={isActive}
                          aria-current={isActive ? 'page' : undefined}
                          onClick={handleNavigationItemClick}
                          sx={{
                            minHeight: 42,
                            borderRadius: 2,
                            ml: 2,
                            pl: 2.5,
                            pr: 1.5,
                            textDecoration: 'none',
                            color: alpha(theme.palette.common.white, 0.82),
                            '&.Mui-selected': {
                              backgroundColor: 'primary.main',
                              color: 'primary.contrastText',
                              boxShadow: theme.shadows[2],
                              '&:hover': {
                                backgroundColor: 'primary.dark',
                              },
                              '& .MuiListItemIcon-root': {
                                color: 'inherit',
                              },
                            },
                            '&:hover': {
                              backgroundColor: alpha(theme.palette.common.white, 0.08),
                            },
                            '&.Mui-focusVisible': {
                              outline: `2px solid ${alpha(theme.palette.primary.main, 0.28)}`,
                              outlineOffset: 1,
                            },
                          }}
                        >
                          <ListItemIcon
                            sx={{
                              minWidth: 34,
                              color: isActive
                                ? 'inherit'
                                : alpha(theme.palette.common.white, 0.64),
                            }}
                          >
                            <ItemIcon fontSize="small" />
                          </ListItemIcon>
                          <ListItemText
                            primary={item.title}
                            primaryTypographyProps={{
                              fontSize: '0.93rem',
                              fontWeight: isActive ? 700 : 500,
                            }}
                          />
                        </ListItemButton>
                      </ListItem>
                    );
                  })}
                </List>
              </Collapse>
            </Box>
          );
        })}
      </List>

      {companyLogoUrl && (
        <>
          <Divider />
          <Box sx={{ px: 2, pt: 1.5, pb: 2 }}>
            <Box
              sx={{
                border: '1px solid',
                borderColor: alpha(theme.palette.common.white, 0.08),
                borderRadius: 2.5,
                backgroundColor: alpha(theme.palette.common.white, 0.04),
                px: 1,
                py: 1,
                minHeight: 96,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Box
                component="img"
                src={companyLogoUrl}
                alt={companyName || t('settings.logo')}
                sx={{
                  display: 'block',
                  width: 'auto',
                  maxWidth: '100%',
                  height: 'auto',
                  maxHeight: 94,
                }}
              />
            </Box>
          </Box>
        </>
      )}
    </Box>
  );

  const drawerContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {drawerHeader}
      <Divider />
      {drawerBody}
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <AppBar
        position="fixed"
        color="primary"
        elevation={0}
        sx={{
          width: { md: `calc(100% - ${desktopAppBarOffset}px)` },
          ml: { md: `${desktopAppBarOffset}px` },
          backgroundImage: 'linear-gradient(135deg, #0A0F1C 0%, #102756 100%)',
          color: theme.palette.common.white,
          borderBottom: '1px solid',
          borderColor: alpha(theme.palette.common.white, 0.08),
          boxShadow: '0 18px 48px rgba(10, 15, 28, 0.22)',
          transition: theme.transitions.create(['margin', 'width'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.standard,
          }),
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label={t('nav.openDrawer', 'Open drawer')}
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          {!isMobile && !desktopSidebarOpen && (
            <Tooltip title={desktopSidebarToggleLabel}>
              <IconButton
                color="inherit"
                aria-label={desktopSidebarToggleLabel}
                edge="start"
                onClick={handleDesktopSidebarToggle}
                sx={{ mr: 2 }}
              >
                <MenuIcon />
              </IconButton>
            </Tooltip>
          )}
          <Typography
            variant="h6"
            noWrap
            component="div"
            sx={{ flexGrow: 1, fontWeight: 700 }}
          >
            {pageTitle}
          </Typography>
          <Tooltip title={t('nav.home', 'Home')}>
            <IconButton
              color="inherit"
              onClick={handleCompanyHome}
              aria-label={t('nav.home', 'Home')}
              sx={{
                ml: 1,
                backgroundColor: alpha(theme.palette.common.white, 0.12),
                '&:hover': {
                  backgroundColor: alpha(theme.palette.common.white, 0.18),
                },
              }}
            >
              <HomeIcon />
            </IconButton>
          </Tooltip>
          <NotificationBell />
          <LanguageSwitcher />
          <Tooltip title={t('auth.logout')}>
            <IconButton
              color="inherit"
              onClick={handleLogout}
              edge="end"
              sx={{ ml: 2 }}
            >
              <Logout />
            </IconButton>
          </Tooltip>
        </Toolbar>
      </AppBar>

      <Box
        component="nav"
        sx={{
          width: { md: desktopSidebarWidth },
          flexShrink: { md: 0 },
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.standard,
          }),
        }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              overflow: 'hidden',
              backgroundImage: 'linear-gradient(180deg, #0A0F1C 0%, #10224A 100%)',
              color: theme.palette.common.white,
              borderRightColor: alpha(theme.palette.common.white, 0.08),
            },
          }}
        >
          {drawerContent}
        </Drawer>

        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            width: desktopSidebarWidth,
            transition: theme.transitions.create('width', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.standard,
            }),
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: desktopSidebarWidth,
              borderRightWidth: desktopSidebarOpen ? `${drawerDividerWidth}px` : 0,
              overflow: 'hidden',
              backgroundImage: 'linear-gradient(180deg, #0A0F1C 0%, #10224A 100%)',
              color: theme.palette.common.white,
              borderRightColor: alpha(theme.palette.common.white, 0.08),
              transition: theme.transitions.create(['border-width', 'width'], {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.standard,
              }),
            },
          }}
          open
        >
          {desktopSidebarOpen ? drawerContent : null}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 1.25,
          width: { md: `calc(100% - ${desktopSidebarWidth}px)` },
          minHeight: '100vh',
          backgroundColor: 'transparent',
          backgroundImage: `
            radial-gradient(circle at top right, rgba(37, 99, 235, 0.08), transparent 22%),
            linear-gradient(180deg, rgba(255, 255, 255, 0.58) 0%, rgba(243, 245, 249, 0.88) 100%)
          `,
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.standard,
          }),
        }}
      >
        <Toolbar />
        <Box
          sx={{
            width: '100%',
            maxWidth: (theme) => `${theme.breakpoints.values.xl}px`,
            mx: 'auto',
          }}
        >
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
};
